import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, Grid, Paper, IconButton, CssBaseline, keyframes,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.4;
  }
  100% {
    transform: scale(1);
    opacity: 0.3;
  }
`;

const Login = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const { switchTheme, themeName } = useTheme();

  // Get the redirect path from location state or use default
  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const userRole = await login(formData.username, formData.password);
      // Navigate to the saved location or default based on role
      if (from === '/dashboard' && userRole === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate(from);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to login. Please check your credentials.');
    }
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <CssBaseline />
      {/* Left Decorative Panel */}
      <Grid
        item
        xs={12}
        sm={6}
        md={7}
        sx={{
          backgroundColor: (theme) => {
            if (themeName === 'glass') {
              return 'rgba(30, 25, 40, 1)';
            }
            return theme.palette.mode === 'light' ? '#FAE8DE' : '#121212';
          },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          position: 'relative',
          overflow: 'hidden',
          transition: 'background-color 0.3s ease'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-20%',
            width: '70%',
            paddingBottom: '70%',
            bgcolor: (theme) => {
              if (themeName === 'glass') {
                return 'rgba(255, 255, 255, 0.04)';
              }
              return theme.palette.mode === 'light' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.05)';
            },
            borderRadius: '50%',
            zIndex: 1,
            animation: `${pulse} 8s ease-in-out infinite`,
          }}
        />
        <Box sx={{ zIndex: 2, textAlign: 'left', width: '100%', maxWidth: 500 }}>
          <Typography variant="h1" sx={{ 
            fontWeight: 'bold', 
            color: themeName === 'glass' ? 'white' : 'text.primary', 
            animation: `${fadeInUp} 1s ease-out`,
            fontSize: { xs: '2.0rem', sm: '3.0rem', md: '3.5rem' }
          }}>
            DigiCardly
          </Typography>
          <Typography variant="h5" sx={{ 
            color: themeName === 'glass' ? 'rgba(255, 255, 255, 0.85)' : 'text.secondary', 
            mt: 2, 
            animation: `${fadeInUp} 1s ease-out 0.2s`, 
            animationFillMode: 'forwards', 
            opacity: 0,
            fontSize: { xs: '1.0rem', sm: '1.3rem' }
          }}>
            The new standard for your digital presence. Create and share your professional portfolio in seconds.
          </Typography>
        </Box>
      </Grid>

      {/* Right Login Panel */}
      <Grid item xs={12} sm={6} md={5} component={Paper} elevation={6} square
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 4, md: 6 },
            width: '100%',
            maxWidth: { xs: '100%', sm: 400 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            width: '100%' 
          }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton onClick={() => switchTheme()} color="inherit">
              {muiTheme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
          </Box>
          <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', mt: 2 }}>
            Login Now
          </Typography>
          <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={formData.username}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />
            {error && (
              <Typography color="error" align="center" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5, 
                borderRadius: '8px', 
                bgcolor: 'black', 
                color: 'white', 
                '&:hover': { bgcolor: '#333' } 
              }}
            >
              Login Now
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Login; 